'use client';

import { useTranslations } from 'next-intl';
import { useState, type ReactNode } from 'react';

import { TrackedAnchor } from '@/components/ancher';
import { TRACKED_EVENT_POSITION } from '@/constants';
import { useInquiryChat } from '@/components/channel-talk';

import { CTA_PILL_CLASS, CtaButton } from '../components/cta-button';
import { SectionTitle } from '../components/section-title';
import { useFaqTracking } from '../components/use-faq-tracking';

const FAQ_KEYS = ['0', '1', '2'] as const;
type FaqKey = (typeof FAQ_KEYS)[number];

const richTags = {
  p: (chunks: ReactNode) => (
    <p className="leading-[28px] [&:not(:first-child)]:mt-[28px]">{chunks}</p>
  ),
  ul: (chunks: ReactNode) => (
    <ul className="list-disc pl-[27px] [&:not(:first-child)]:mt-[8px]">
      {chunks}
    </ul>
  ),
  li: (chunks: ReactNode) => <li className="leading-[28px]">{chunks}</li>,
  br: () => <br />,
};

const MORE_QUESTIONS_CLASS = `${CTA_PILL_CLASS} bg-[#ff7767] focus-visible:ring-[#c2412f] px-[34px]`;
const CONTACT_CTA_CLASS = `${CTA_PILL_CLASS} bg-[#040435] focus-visible:ring-[#040435] px-[34px]`;

function ChevronDown({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden className={className}>
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface FaqRowProps {
  faqKey: FaqKey;
  question: ReactNode;
  answer: ReactNode;
  isOpen: boolean;
  onToggle: (key: FaqKey) => void;
}

function FaqRow({ faqKey, question, answer, isOpen, onToggle }: FaqRowProps) {
  const contentId = `neo-faq-content-${faqKey}`;
  return (
    <li className="border-b border-solid border-b-[#E4E6EB] first:border-t first:border-t-[#E4E6EB]">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={contentId}
        onClick={() => onToggle(faqKey)}
        className="flex w-full cursor-pointer items-center gap-10 px-0 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#040435] focus-visible:ring-offset-2 md:px-[10px] md:py-5"
      >
        <span className="flex-1 text-base font-medium leading-6 tracking-[-0.176px] text-[#2b2e33] md:text-lg md:leading-7 md:tracking-[-0.256px]">
          {question}
        </span>
        <span className="flex size-10 shrink-0 items-center justify-center">
          <ChevronDown
            className={`size-5 text-[#BEC2CC] transition-transform duration-300 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </span>
      </button>
      <div
        id={contentId}
        aria-hidden={!isOpen}
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-0 pb-5 text-base font-medium leading-[28px] tracking-[-0.256px] text-[#6C7380] md:px-[10px] md:text-lg">
            {answer}
          </div>
        </div>
      </div>
    </li>
  );
}

export default function FaqSection() {
  const t = useTranslations('Neo.faqSection');
  const trackFaq = useFaqTracking();
  const inquiryChatProps = useInquiryChat();

  const [openMap, setOpenMap] = useState<Record<FaqKey, boolean>>(
    () =>
      Object.fromEntries(FAQ_KEYS.map(key => [key, false])) as Record<
        FaqKey,
        boolean
      >,
  );

  const handleToggle = (key: FaqKey) => {
    const willOpen = !openMap[key];
    trackFaq(key, willOpen);
    setOpenMap(prev => ({ ...prev, [key]: willOpen }));
  };

  return (
    <section className="bg-white px-5 py-8 md:py-[60px]">
      <div className="mx-auto flex w-full max-w-[960px] flex-col items-stretch gap-y-4 md:gap-y-8">
        <SectionTitle className="md:text-[#2b2e33]">{t('title')}</SectionTitle>

        <ul>
          {FAQ_KEYS.map(key => (
            <FaqRow
              key={key}
              faqKey={key}
              question={t(`items.${key}.question`)}
              answer={t.rich(`items.${key}.answer`, richTags)}
              isOpen={openMap[key]}
              onToggle={handleToggle}
            />
          ))}
        </ul>

        <div className="flex items-center justify-center gap-4 md:gap-5">
          <TrackedAnchor
            href={t('cta.moreQuestionsHref')}
            label="neo-faq-more-questions"
            position="main_cta"
            className={MORE_QUESTIONS_CLASS}
          >
            {t('cta.moreQuestions')}
          </TrackedAnchor>
          <CtaButton
            label="contact-us"
            position={TRACKED_EVENT_POSITION.MAIN_CTA}
            {...inquiryChatProps}
            className={CONTACT_CTA_CLASS}
          >
            {t('cta.contactUs')}
          </CtaButton>
        </div>
      </div>
    </section>
  );
}
